import React from "react";

/* ─────────────────────────────────────────────────────────────────────────
 *  <Avatar /> — circular user image with graceful "initials" fallback.
 *
 *  Props
 *  ─────
 *    src    – photo URL (any /api/uploads/... or absolute). Falsy → initials.
 *    name   – string used to derive initials and the alt text.
 *    size   – pixel size of the circle. Default 32.
 *    color  – seed for the fallback background colour (default: `name`).
 *    className – extra utility classes (e.g. `ring-2 ring-white`).
 *    testid – data-testid (will be `${testid}-img` / `${testid}-fallback`).
 * ──────────────────────────────────────────────────────────────────────── */

const PALETTE = [
  "#0047AB", "#2563EB", "#7C3AED", "#DB2777",
  "#DC2626", "#EA580C", "#CA8A04", "#16A34A",
  "#0891B2", "#475569",
];

function hashCode(s = "") {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function initialsFrom(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Avatar({
  src,
  name = "",
  size = 32,
  color,
  className = "",
  testid,
}) {
  const [broken, setBroken] = React.useState(false);
  const dim = { width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)) };
  const bg = PALETTE[hashCode(color || name) % PALETTE.length];

  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        onError={() => setBroken(true)}
        className={`inline-block rounded-full object-cover bg-paper border border-rule shrink-0 ${className}`}
        style={dim}
        data-testid={testid ? `${testid}-img` : undefined}
      />
    );
  }
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full text-white font-semibold uppercase shrink-0 select-none ${className}`}
      style={{ ...dim, background: bg, lineHeight: 1 }}
      data-testid={testid ? `${testid}-fallback` : undefined}
      aria-label={name || "avatar"}
    >
      {initialsFrom(name)}
    </span>
  );
}
